import React, { type ReactNode, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { GroupGridLoading, GroupGridNotification } from '@pkg/components/Sections/Groups';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { FilterChips } from '../FilterChips';

import { useItemData } from './hooks';
import { ModernGrid } from './sections';

import { ErrorWrapper, Pagination } from './styles';

export interface GroupGridProps {
    children?: ReactNode;
}

export function GroupGrid({ children }: GroupGridProps) {
    const [translate] = useTranslation(['groups']);

    const { ref, inView } = useInView();
    const { items, total, filtersTotal, error, loading, fetching, hasNextPage, isPreviousData, fetchNextPage } =
        useItemData();

    useEffect(() => {
        if (inView && !loading && !fetching) fetchNextPage();
    }, [inView, loading, fetching, fetchNextPage]);

    if (loading) return <GroupGridLoading />;

    if (error) {
        const isForbidden = (error as Error)?.toString().includes('Forbidden');
        const header = translate(
            isForbidden ? 'groups_private_error_with_filters_title' : 'groups_error_with_filters_title',
        );
        const body = translate(
            isForbidden ? 'groups_private_error_with_filters_body' : 'groups_error_with_filters_body',
        );
        return (
            <ErrorWrapper>
                <GroupGridNotification heading={header} subheading={body} />
                <Button color="transparent" to="/">
                    {translate('groups_error_get_me_back_home')}
                </Button>
            </ErrorWrapper>
        );
    }

    if (!total && !filtersTotal) {
        return (
            <GroupGridNotification
                heading={translate('groups_no_results_title')}
                subheading={translate('groups_no_results_body')}
            />
        );
    }

    if (!total) {
        return (
            <GroupGridNotification
                heading={translate('groups_no_results_with_filters_title')}
                subheading={translate('groups_no_results_with_filters_body')}
            />
        );
    }

    return (
        <div>
            <FilterChips total={total} />

            <ModernGrid items={items} />
            {children}

            {hasNextPage ? (
                <Pagination>
                    <Button
                        ref={ref}
                        disabled={isPreviousData || !hasNextPage || fetching}
                        loading={fetching}
                        onClick={() => fetchNextPage()}
                    >
                        {translate('groups_btn_load_more')}
                    </Button>
                </Pagination>
            ) : null}
        </div>
    );
}
